import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'react-bootstrap-icons';
import { useNavigate, useLocation } from 'react-router-dom';

const FAQ = ({ showAll = false, id, layout = 'side' }) => {
    const [openIds, setOpenIds] = useState(new Set());
    const navigate = useNavigate();
    const location = useLocation();

    const faqSections = [
        {
            title: 'Overview',
            items: [
                {
                    id: 'overview-1',
                    question: 'What is FGC TMS?',
                    answer: 'FGC TMS is a cloud-based Trading and Trade Management System built specifically for fixed income broker dealers, both sell-side and buy-side firms. It is a full replacement for Legacy Systems, such as TOMS, delivering superior customized trading, post-trade automation, real-time reconciliation, and advanced P&L and payout reports at a significantly lower cost. The platform manages the entire trade lifecycle from positions, quotes, orders, trading, execution, clearing, settlement, regulatory reporting, and risk and compliance. It provides a single, accurate source of truth for traders, operations, compliance, and management.'
                },
                {
                    id: 'overview-2',
                    question: 'Who is FGC TMS designed for?',
                    answer: 'FGC TMS is designed for sell-side and buy-side broker dealers with small to large trading desks and moderate-to-high daily trading volumes. It is especially ideal for firms where Legacy System is too expensive, inflexible, or operationally burdensome. These firms typically face profit margin pressure and rely heavily on broken and manual processes. FGC TMS helps them regain efficiency, transparency, and control without enterprise-level costs.'
                },
                {
                    id: 'overview-3',
                    question: 'How is FGC TMS different from Legacy System?',
                    answer: 'FGC TMS is a modern, cloud-native platform built to address the operational gaps left by the Legacy System. While Legacy System is costly, rigid, and heavily dependent on manual processes, FGC TMS offers integrated trading, customized trade workflows, real-time clearing, reporting, reconciliation, automated P&L and payouts. It is significantly lower in cost and designed to evolve continuously. Most importantly, it reduces operational risk instead of adding to it. We can customize the trading, reports, and workflows to suit your needs.'
                },
                {
                    id: 'overview-4',
                    question: 'Can FGC TMS really replace Legacy System?',
                    answer: 'Yes. FGC TMS is already live as a full Legacy System replacement for multiple clients in the US and UK. It supports all core Legacy System functions, including trade blotters, clearing and settlement workflows, positions and P&L, finance charge allocation, compliance controls, and regulatory reporting. Clients typically run FGC TMS in parallel with Legacy System before switching fully. This ensures a smooth, validated transition with minimal risk.'
                },
                {
                    id: 'overview-5',
                    question: 'What is the biggest reason firms switch to FGC TMS?',
                    answer: 'Firms switch to FGC TMS for four core reasons: integrated and customized trading workflows, significant cost savings, operational sanity, and enhanced risk and compliance controls. The platform replaces swivel chair trading, manual, error-prone processes with automation and real-time visibility. It turns fixed income operations from a daily fire drill into a controlled, predictable process. This directly improves profitability and control.'
                },
                {
                    id: 'overview-6',
                    question: 'Why should we trust a non-Legacy System alternative?',
                    answer: 'FGC TMS is live in production and solving real operational challenges today. Built by fixed income industry veterans, it is SOC2 Type II and ISO certified, ensuring strong security and compliance standards. Clients can validate the system in parallel before committing, reducing risk. Trust is earned through transparency, performance, and proven results - not brand size alone.'
                }
            ]
        },
        {
            title: 'Migration & Implementation',
            items: [
                {
                    id: 'migration-1',
                    question: 'How risky is it to migrate off Legacy System?',
                    answer: 'Migration risk is mostly eliminated with FGC TMS because it is designed to run in parallel with existing systems. Firms can validate trades, P&L, payouts, and exceptions side by side with Legacy System before making any switch. There is no forced cutover or big bang migration. Most clients complete the transition comfortably within one to three months, depending on scope.'
                },
                {
                    id: 'migration-2',
                    question: 'How much effort is required from our IT team?',
                    answer: 'Very little to no internal IT effort is required. FGC TMS is a fully managed SaaS platform with no on-prem infrastructure, upgrades, or maintenance responsibilities for the client. Typically, clients only need to facilitate introductions to ECNs and custodians and assign an operations contact for validation. No dedicated internal IT project or long implementation cycle is needed.'
                },
                {
                    id: 'migration-3',
                    question: 'Can we start small and expand later?',
                    answer: 'Yes. Many clients begin with post-trade workflows such as reconciliation, P&L, and compliance. They can expand into front-office capabilities like Quotes, Orders, RFQs, and List management, quoting, and execution both. FGC TMS is designed for incremental adoption as you choose, allowing firms to grow functionality without disruption. This keeps risk low while delivering immediate value.'
                }
            ]
        },
        {
            title: 'Capabilities & Controls',
            items: [
                {
                    id: 'capabilities-1',
                    question: 'Which asset classes are supported?',
                    answer: 'FGC TMS supports a broad range of fixed income products, including corporates, municipals, treasuries, agencies, ABS, MBS, floaters, money markets, and international bonds. The platform includes a robust fixed income analytics engine that handles complex calculations such as DV01, spreads, durations, and convexity. This ensures accurate risk, P&L, and position analysis across asset classes.'
                },
                {
                    id: 'capabilities-2',
                    question: 'Which venues and custodians do you integrate with?',
                    answer: 'FGC TMS integrates with all major fixed income ECNs and ATSs, including Tradeweb, MarketAxess, Trumid, ICE BondPoint, Bonds.com, TMC, MuniBroker and Bloomberg. It also connects seamlessly with leading custodians and clearers such as Pershing, RBC, Wedbush, and Mirae. New venues or custodians can be onboarded quickly as business needs evolve. FGC TMS can integrate with existing liquidity aggregators such as LBA, and others.'
                },
                {
                    id: 'capabilities-3',
                    question: 'What is "Three-Way Reconciliation"?',
                    answer: 'Three-Way Reconciliation continuously matches what was traded on the ECN, what the custodian shows, and what was reported to regulators such as TRACE. This provides real-time visibility into trade status using clear indicators and immediate identification of breaks or exceptions. It creates a clean audit trail for compliance and significantly reduces end-of-day operational fire drills. Ops teams know exactly where every trade stands at all times.'
                },
                {
                    id: 'capabilities-4',
                    question: 'How does FGC TMS handle P&L and payouts?',
                    answer: 'FGC TMS fully automates daily P&L and monthly payout calculations, eliminating spreadsheet-based workflows. It accurately calculates realized and unrealized P&L, allocates funding and finance charges, applies firm-specific markups, and manages loss of carryovers. Payout-ready reports are typically available by the third business day. Every figure is fully auditable with drill-down visibility to the underlying trades.'
                },
                {
                    id: 'capabilities-5',
                    question: 'How are finance charges allocated?',
                    answer: 'FGC TMS takes complex, firm-level finance and funding charges received from custodians and allocates them accurately down to desks, traders, and individual positions. This removes guesswork and eliminates common payout disputes. The allocation logic is transparent, consistent, and fully auditable. As a result, trust improves across traders, operations, and management.'
                },
                {
                    id: 'capabilities-6',
                    question: 'Does FGC TMS support regulatory reporting?',
                    answer: 'Yes. FGC TMS supports regulatory workflows including TRACE and MiFID reporting, monitoring and exception management working with Custodians and third-parties for regulatory reporting. It provides structured documentation and visibility required for regulatory reviews. Best-execution capture is supported internally as well as through integrations such as BondWave. Native and partner-based reporting options are available depending on regulatory and client requirements.'
                },
                {
                    id: 'capabilities-7',
                    question: 'What compliance and risk controls are included?',
                    answer: 'FGC TMS delivers comprehensive, real-time risk and compliance controls purpose-built for fixed income trading, including per-trade, daily, and net limits; DV01, sector, and counterparty-based risk controls; IG and HY segmentation; and security aging alerts. Risk utilization is updated instantly as trades are executed and displayed through intuitive visual indicators and graphical progress bars, enabling traders to quickly assess available headroom, exposure, and potential breaches with or without disrupting execution flow (soft block vs hard block). A centralized compliance blotter records all risk events and user actions with a complete audit trail, with all limits and alerts.'
                },
                {
                    id: 'capabilities-8',
                    question: 'Can FGC TMS be customized to our workflows?',
                    answer: 'Yes. Customization is a core part of FGC TMS\'s design. Screens, reports, limits, and workflows can be tailored to match how your desk actually operates. Each client runs in a dedicated, isolated environment, ensuring changes do not impact others. The platform adapts to your workflow rather than forcing you to adapt to the system.'
                },
                {
                    id: 'capabilities-9',
                    question: 'How secure is FGC TMS?',
                    answer: 'FGC TMS is built with strong enterprise-grade security controls. It uses role-based access, strict data segregation, client-level environment isolation, and full audit logging. Users only see the data relevant to their role and permissions. The platform is designed to meet the security expectations of regulated financial institutions.'
                },
                {
                    id: 'capabilities-10',
                    question: 'Where is the system hosted?',
                    answer: 'FGC TMS is hosted in secure, cloud-based environments built on AWS infrastructure. The platform is fully managed by Fintech Global Center, including monitoring, upgrades, patches, and performance management. Clients benefit from a stable, scalable environment without the burden of internal infrastructure. The system remains current without disruption to daily operations.'
                }
            ]
        },
        {
            title: 'Ownership, Support & Pricing',
            items: [
                {
                    id: 'ownership-1',
                    question: 'Who owns and operates FGC TMS?',
                    answer: 'FGC TMS is built, owned, and operated by Fintech Global Center, a firm with over 30 years of experience building global trading systems. The team has deep fixed income domain expertise and operates live production systems in the US and UK. Development, support, onboarding, and operations are all handled in-house. Fintech Global Center does not just sell software - it runs mission-critical trading platforms.'
                },
                {
                    id: 'ownership-2',
                    question: 'What kind of support do you provide?',
                    answer: 'Fintech Global Center provides dedicated onboarding, ongoing production support, and continuous enhancements. Clients work directly with domain experts who understand fixed income workflows, not generic call centers. Support is proactive, focused on preventing issues rather than reacting to them. The goal is long-term operational stability and client success.'
                },
                {
                    id: 'ownership-3',
                    question: 'How does pricing work?',
                    answer: 'FGC TMS is offered as a transparent SaaS subscription, typically priced 50-80% lower than current Legacy System. There are no hidden infrastructure or upgrade costs. Pricing depends on scope, integration, and functionality required. The model aligns cost with value and scale.'
                }
            ]
        }
    ];

    const allQuestions = faqSections.flatMap((section) => section.items);
    const displayedQuestionIds = new Set((showAll ? allQuestions : allQuestions.slice(0, 6)).map((item) => item.id));
    const displayedSections = faqSections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => displayedQuestionIds.has(item.id))
        }))
        .filter((section) => section.items.length > 0);

    // Handle "View More" button click
    const handleViewMore = () => {
        navigate('/how-it-works#faq');
    };

    // Scroll to FAQ section when arriving from homepage with hash
    useEffect(() => {
        if (location.hash === '#faq' && showAll) {
            const faqSection = document.getElementById('faq');
            if (faqSection) {
                setTimeout(() => {
                    faqSection.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location.hash, showAll]);

    useEffect(() => {
        const allDisplayedIds = new Set(displayedSections.flatMap((section) => section.items.map((item) => item.id)));

        setOpenIds((prev) => {
            const filtered = new Set([...prev].filter((id) => allDisplayedIds.has(id)));

            if (filtered.size === prev.size) {
                const isSame = [...filtered].every((id) => prev.has(id));
                if (isSame) {
                    return prev;
                }
            }

            return filtered;
        });
    }, [displayedSections]);

    const toggleQuestion = (questionId) => {
        setOpenIds((prev) => {
            const updated = new Set(prev);
            if (updated.has(questionId)) {
                updated.delete(questionId);
            } else {
                updated.add(questionId);
            }
            return updated;
        });
    };

    return (
        <section
            id={id}
            className="w-full bg-[#090E1B] py-20 text-white min-h-[600px] flex items-center justify-center"
        >
            <div className={`site-shell flex flex-col gap-[50px] ${layout === 'side' ? 'lg:flex-row lg:gap-[26px]' : 'lg:gap-[50px]'}`}>

                <div className={`w-full flex flex-col gap-[20px] ${layout === 'side' ? 'lg:w-[514px]' : 'text-center'}`}>
                    <h2 className="font-oswald font-bold text-3xl md:text-5xl leading-none uppercase">
                        FREQUENTLY ASKED QUESTIONS
                    </h2>
                    <p className="font-inter text-md md:text-lg text-white/85 leading-tight">
                        Get answers to common questions about FGC TMS
                    </p>
                </div>

                <div className={`w-full flex flex-col ${layout === 'side' ? 'lg:w-[730px]' : ''}`}>
                    {displayedSections.map((section, sectionIndex) => (
                        <div key={section.title} className="w-full">
                            {(showAll || section.title !== 'Overview') && (
                                <h3 className={`mb-2 font-inter text-2xl md:text-2xl font-bold text-[#00C2FF] md:text-[36px] ${sectionIndex === 0 ? 'mt-0' : 'mt-8 pt-2'}`}>
                                    {section.title}
                                </h3>
                            )}

                            {section.items.map((item) => {
                                const isOpen = openIds.has(item.id);

                                return (
                                    <div key={item.id} className="w-full border-b border-white/35">
                                        <button
                                            onClick={() => toggleQuestion(item.id)}
                                            className="w-full flex justify-between items-center py-4 text-left cursor-pointer hover:opacity-80 transition-opacity"
                                        >
                                            <h4 className="font-bold text-[18px] md:text-[20px] leading-tight font-inter pr-4">
                                                {item.question}
                                            </h4>
                                            <ChevronDown
                                                size={20}
                                                className={`flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                                            />
                                        </button>

                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1200px] pb-5' : 'max-h-0'}`}
                                        >
                                            <p className="font-inter text-[#B9C4D8] text-[14px] md:text-[15px] leading-relaxed">
                                                {item.answer}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {!showAll && (
                        <button
                            onClick={handleViewMore}
                            className="mt-8 self-start px-10 py-3 bg-transparent border-2 border-white text-white font-inter font-semibold text-[16px] rounded-[58px] hover:bg-white hover:text-[#090E1B] transition-all duration-300"
                        >
                            View More FAQs
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FAQ;
